/**
 * API client.
 *
 * Thin on purpose. Session auth is a cookie, so there is no token plumbing —
 * `credentials: 'include'` and the browser does the rest.
 */

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public required?: string[],
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const installed = (): boolean => {
  try {
    return window.matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
  } catch { return false; }
};

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // Content-Type is only set when there IS a body. Declaring application/json
  // on a bodyless POST makes Fastify reject the request with "Body cannot be
  // empty when content-type is set to 'application/json'" — which breaks every
  // action that takes no payload: logout, password reset, and the instance
  // start/stop/reboot calls.
  const headers: Record<string, string> = { ...(init?.headers as Record<string, string>) };
  // Opened from the home screen (an installed app) rather than a browser tab:
  // the server gives such a sign-in a longer life.
  if (installed()) headers['X-Display-Mode'] = 'standalone';
  if (init?.body !== undefined && init.body !== null) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(`/api${path}`, {
    ...init,
    credentials: 'include',
    headers,
  });

  const text = await response.text();
  const body = text ? (JSON.parse(text) as Record<string, unknown>) : {};

  if (!response.ok) {
    throw new ApiError(
      response.status,
      (body.error as string) ?? 'error',
      (body.message as string) ?? `Request failed (${response.status})`,
      body.required as string[] | undefined,
    );
  }

  return body as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  delete: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'DELETE', body: body ? JSON.stringify(body) : undefined }),
};

// ===========================================================================
// Types mirrored from the server
// ===========================================================================

export interface User {
  id: string;
  email: string;
  name: string;
  permissions: string[];
  groupIds: string[];
  isAdmin: boolean;
  mustChangePassword: boolean;
  totpEnabled: boolean;
  /**
   * Permissions this user holds that do nothing until an authenticator is
   * enrolled. Non-empty with totpEnabled false means the UI should prompt.
   */
  mfaBlockedPermissions: string[];
}

export interface Instance {
  instanceId: string;
  region: string;
  name: string | null;
  state: string;
  instanceType: string | null;
  publicIp: string | null;
  privateIp: string | null;
  launchTime: string | null;
  availabilityZone: string | null;
  platform: string | null;
  templateId: string | null;
  groupId: string | null;
  ownerEmail: string | null;
  managed: boolean;
  /** Hand-built and adopted: started and stopped from the Adopted page only. */
  adopted?: boolean;
  tags: Record<string, string>;
  usdPerHour: number | null;
  currentSessionUsd: number | null;
  pendingOperation: string | null;
}

export interface MonitorStatus {
  running: boolean;
  lastRunAt: string | null;
  lastDurationMs: number | null;
  lastError: string | null;
  instanceCount: number;
  regionErrors: { region: string; message: string }[];
  consecutiveFailures: number;
}

/** One zone's answer the last time this dashboard asked AWS for this size. */
export interface CapacitySighting {
  az: string;
  ok: boolean;
  at: string;
}

/** A launch AWS had no room for, being retried by the dashboard. */
export interface LaunchWait {
  id: string;
  templateId: string;
  templateName: string;
  sizeId: string;
  sizeLabel: string;
  instanceType: string;
  region: string;
  name: string;
  requestedBy: string;
  status: 'waiting' | 'launched' | 'failed' | 'cancelled';
  attempts: number;
  zones: number;
  startedAt: string;
  until: string;
  lastAttemptAt: string;
  nextAttemptAt: string | null;
  message: string;
  instanceId: string | null;
  /** Every size the template offers. */
  sizeOptions: {
    id: string; label: string; instanceType: string; vcpu: number; gpu: string | null; usdPerHour: number | null;
  }[];
  /** Tried in this order on every attempt: the first choice, then any added. */
  trying: string[];
  /** Sizes AWS refused for a reason waiting will not fix, e.g. vCPU quota. */
  skipped: { sizeId: string; reason: string }[];
  launchedSizeId: string | null;
}

export interface TemplateSize {
  id: string;
  instanceType: string;
  label: string;
  vcpu: number;
  memoryGiB: number;
  gpu?: string;
  livePricePerHour: number | null;
  /** What recent launches and starts found in each zone. Newest first. */
  capacity?: CapacitySighting[];
  bestFor?: string;
  caution?: string;
  default?: boolean;
  deprioritised?: boolean;
}

/**
 * A launch option, exactly as the template declares it.
 *
 * The launch form used to hardcode its fields, which meant a template could
 * declare an option and never be asked about it. The multiviewer declared two -
 * the grid size and whether to record - and neither ever appeared, so every
 * multiviewer launched 2x2 with recording off whatever the operator wanted.
 *
 * Rendering these instead means adding an option to a template is enough to be
 * asked about it.
 */
export interface TemplateOption {
  id: string;
  label: string;
  /**
   * `generated-secret` is made by the server and never shown. `secret` is a
   * value an operator would type; none is currently offered.
   */
  type: 'select' | 'boolean' | 'secret' | 'generated-secret' | 'text' | 'textarea';
  default?: string | boolean | null;
  choices?: Array<string | number>;
  choiceLabels?: Record<string, string>;
  description?: string;
  note?: string;
  /** Hint inside an empty text box, for `secret` options. */
  placeholder?: string;
  /** Shown when the option is set to true. */
  warnWhenTrue?: string;
  /** Shown when the option is set to the matching value. */
  warnOnValue?: Record<string, string>;
  /**
   * Declared but not offered, because the answer is no longer a real choice -
   * the golden image already ships it. Still sent at its default so a launch
   * from a bare image behaves as before.
   */
  hidden?: boolean;
}

export interface Template {
  id: string;
  name: string;
  summary: string;
  description: string;
  platform: string;
  sizes: TemplateSize[];
  storage: {
    root: { sizeGiBOptions: number[]; defaultSizeGiB: number };
  };
  options: TemplateOption[];
  ports: Array<Record<string, unknown>>;
  provisioning: { expectedDurationMinutes: number; steps: string[] };
  manualSteps?: Array<Record<string, unknown>>;
  images: Array<{ id: string; ami_id: string; name: string; state: string; is_default: number; created_at: string }>;
  state: { default_ami_id: string | null; launch_count: number; last_launched_at: string | null };
  storageCostPerDay: number;
}

export interface ResourceGroup {
  id: string;
  name: string;
  description: string;
  colour: string;
  cost_centre: string | null;
  user_count: number;
  instance_count: number;
}

export interface CostPoint {
  period: string;
  amount: number;
  groups?: { key: string; amount: number }[];
}

export interface LiveCosts {
  runningNow: number;
  usdPerHourNow: number;
  todayUsd: number;
  monthUsd: number;
  byInstance: {
    instanceId: string;
    name: string | null;
    instanceType: string;
    usdPerHour: number | null;
    sessionUsd: number | null;
    startedAt: string;
  }[];
}

// ===========================================================================
// Formatting
// ===========================================================================

export function formatUsd(amount: number | null | undefined, decimals = 2): string {
  if (amount === null || amount === undefined) return '—';
  // AWS prices in USD. Labelled, because a bare "$" reads as NZD here.
  return `US$${amount.toFixed(decimals)}`;
}

export function formatUptime(since: string | null): string {
  if (!since) return '—';

  const ms = Date.now() - new Date(since).getTime();
  if (ms < 0) return '—';

  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return `${minutes}m`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m`;

  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
}

export function formatRelative(iso: string | null): string {
  if (!iso) return 'never';

  // SQLite datetimes come back without a zone; they are UTC.
  const withZone = iso.includes('T') || iso.endsWith('Z') ? iso : `${iso.replace(' ', 'T')}Z`;
  const seconds = Math.floor((Date.now() - new Date(withZone).getTime()) / 1000);

  if (seconds < 10) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

/** Maps an EC2 state to a status colour role. Never colour alone — always paired with the label. */
export function stateColour(state: string): string {
  switch (state) {
    case 'running':
      return 'var(--color-good)';
    case 'pending':
    case 'stopping':
    case 'shutting-down':
      return 'var(--color-warning)';
    case 'stopped':
      return 'var(--color-ink-muted)';
    case 'terminated':
      return 'var(--color-critical)';
    default:
      return 'var(--color-ink-muted)';
  }
}
